import { useState, useEffect } from "react";
import { analyzeTransactions } from "../analysis";
import { updateSettings, setTransactionFlag } from "../api";
import { generateReport } from "../pdfExport";
import { fmt } from "../formatters";
import { FONT, COLORS, GRADIENT, FontLink } from "../styles";
import SettingsModal from "./SettingsModal";
import OverviewTab from "./tabs/OverviewTab";
import CashflowTab from "./tabs/CashflowTab";
import AdvancesTab from "./tabs/AdvancesTab";
import TransactionsTab from "./tabs/TransactionsTab";
import RiskTab from "./tabs/RiskTab";
import UnderwritingTab from "./tabs/UnderwritingTab";

const TABS = ["overview", "cashflow", "advances", "transactions", "risk", "underwriting"];
const DAY_OPTIONS = [30, 60, 90, 120, 180, 365];

export default function Dashboard({ transactions, accounts, merchantName, merchantId, settings, onNewAnalysis, onLogout, onChangeDays, currentDays, onTransactionsUpdate }) {
  const [activeTab, setActiveTab] = useState("overview");
  const [mounted, setMounted] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [localSettings, setLocalSettings] = useState(settings);

  useEffect(() => { setTimeout(() => setMounted(true), 100); }, []);
  useEffect(() => { setLocalSettings(settings); }, [settings]);

  const analysis = analyzeTransactions(transactions, localSettings);
  const { currentBalance, balanceAlerts } = analysis;

  const handleExportPDF = () => { generateReport(merchantName, analysis, accounts); };

  const handleSaveSettings = async (newSettings) => {
    const saved = await updateSettings(newSettings);
    setLocalSettings(saved);
  };

  const handleFlagChange = async (mId, txnKey, flag) => {
    await setTransactionFlag(mId, txnKey, flag);
    const updated = transactions.map((t) => {
      const key = t.txn_key || `${t.date}|${t.name}|${t.amount}`;
      if (key === txnKey) return { ...t, flag };
      return t;
    });
    onTransactionsUpdate(updated);
  };

  const renderTab = () => {
    switch (activeTab) {
      case "overview":     return <OverviewTab analysis={analysis} />;
      case "cashflow":     return <CashflowTab analysis={analysis} transactions={transactions} />;
      case "advances":     return <AdvancesTab analysis={analysis} transactions={transactions} />;
      case "transactions": return <TransactionsTab transactions={transactions} merchantId={merchantId} onFlagChange={handleFlagChange} />;
      case "risk":         return <RiskTab analysis={analysis} transactions={transactions} />;
      case "underwriting": return <UnderwritingTab transactions={transactions} merchantName={merchantName} />;
      default:             return null;
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: COLORS.bg, color: COLORS.text, fontFamily: FONT, opacity: mounted ? 1 : 0, transition: "opacity 0.4s ease" }}>
      <FontLink />

      {showSettings && <SettingsModal settings={localSettings} onSave={handleSaveSettings} onClose={() => setShowSettings(false)} />}

      {/* HEADER */}
      <div style={{ background: COLORS.card, borderBottom: `1px solid ${COLORS.border}`, padding: "0 28px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 60 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ width: 32, height: 32, background: GRADIENT, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontSize: 14, fontWeight: 800, color: "#fff" }}>S</span>
            </div>
            <span style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>Starfish Advance</span>
            <span style={{ fontSize: 11, background: COLORS.greenLight, color: COLORS.green, padding: "3px 10px", borderRadius: 6, fontWeight: 500 }}>Connected</span>
            {merchantName && <span style={{ fontSize: 13, color: COLORS.primary, fontWeight: 600 }}>{merchantName}</span>}
            {accounts[0] && <span style={{ fontSize: 12, color: COLORS.dim }}>{accounts[0].name}</span>}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 11, color: COLORS.dim, fontWeight: 500 }}>Current Balance</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: currentBalance < 5000 ? COLORS.red : currentBalance < 15000 ? COLORS.orange : COLORS.green }}>{fmt(currentBalance)}</div>
            </div>
            {balanceAlerts.length > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, background: COLORS.redLight, padding: "5px 12px", borderRadius: 6 }}>
                <span style={{ width: 6, height: 6, background: COLORS.red, borderRadius: "50%", display: "inline-block", animation: "pulse 1.5s infinite" }} />
                <span style={{ fontSize: 12, color: COLORS.red, fontWeight: 600 }}>{balanceAlerts.length} Alert{balanceAlerts.length > 1 ? "s" : ""}</span>
              </div>
            )}
          </div>
        </div>

        {/* Tabs + Actions row */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", gap: 0 }}>
            {TABS.map((tab) => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                style={{ background: "none", border: "none", cursor: "pointer", padding: "10px 18px", fontSize: 13, fontWeight: activeTab === tab ? 600 : 400, color: activeTab === tab ? COLORS.primary : COLORS.muted, borderBottom: `2px solid ${activeTab === tab ? COLORS.primary : "transparent"}`, transition: "all 0.2s", fontFamily: FONT, textTransform: "capitalize" }}>
                {tab}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, paddingBottom: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 11, color: COLORS.dim, fontWeight: 500 }}>Range</span>
              <select value={currentDays} onChange={(e) => onChangeDays(parseInt(e.target.value))}
                style={{ background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: "4px 8px", fontSize: 12, color: COLORS.textSecondary, fontFamily: FONT, outline: "none", cursor: "pointer" }}>
                {DAY_OPTIONS.map((d) => (<option key={d} value={d}>{d} days</option>))}
              </select>
            </div>
            <button onClick={handleExportPDF} style={{ background: COLORS.blueLight, border: "none", borderRadius: 6, padding: "5px 14px", fontSize: 12, color: COLORS.primary, cursor: "pointer", fontFamily: FONT, fontWeight: 500 }}>PDF Report</button>
            <button onClick={() => setShowSettings(true)} style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: "5px 14px", fontSize: 12, color: COLORS.textSecondary, cursor: "pointer", fontFamily: FONT, fontWeight: 500 }}>Settings</button>
            <button onClick={onNewAnalysis} style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: "5px 14px", fontSize: 12, color: COLORS.textSecondary, cursor: "pointer", fontFamily: FONT, fontWeight: 500 }}>New Analysis</button>
            <button onClick={onLogout} style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: "5px 14px", fontSize: 12, color: COLORS.red, cursor: "pointer", fontFamily: FONT, fontWeight: 500 }}>Sign Out</button>
          </div>
        </div>
      </div>

      <div style={{ padding: "24px 28px", maxWidth: 1260 }}>
        {renderTab()}
      </div>

      <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }`}</style>
    </div>
  );
}
